// Refonte du suivi — geste 1 : préparer la version suivante (lot 4, sans IA).
//
// Trois raisons possibles, un bouton chacune. Anissa part de ses notes du
// rendez-vous, d'une demande de la cliente, ou d'un changement de phase. Rien
// d'autre à remplir : la nouvelle version est une copie exacte de la
// précédente, qu'elle modifie ensuite.

import { useState } from 'react';
import {
  MOTIFS_PREPARER, formaterDate, phaseSuivante, preparationPossible, rendezVousRelus,
} from './suiviEcran';

export default function Preparer({
  client,
  protocolPhases = null,
  prochaineVersion = 'V2',
  onPreparer,
  occupe = false,
  erreur = null,
  motifInitial = null,
  // Lot 6 : « À regarder en premier », montré pour un rendez-vous.
  aRegarder = null,
}) {
  const rendezVous = rendezVousRelus(client);
  const phase = phaseSuivante(protocolPhases);
  const [motif, setMotif] = useState(motifInitial);
  const [rdvOrdinal, setRdvOrdinal] = useState(rendezVous[0]?.ordinal ?? null);
  const [demande, setDemande] = useState('');
  const rdvChoisi = rendezVous.find((rdv) => rdv.ordinal === rdvOrdinal) || null;
  const possible = preparationPossible({ motif, rdvOrdinal, rendezVous, phase }) && !occupe;

  return (
    <section className="jrn-block suivi-preparer">
      <div className="jrn-block__head">
        <h3 className="jrn-block__title">Préparer la {prochaineVersion}</h3>
      </div>
      <p className="jrn-block__intro">Pourquoi cette nouvelle version ?</p>
      <div className="jrn-actions" role="radiogroup" aria-label="Raison de la nouvelle version">
        {MOTIFS_PREPARER.map((option) => (
          <button
            key={option.id}
            type="button"
            role="radio"
            aria-checked={motif === option.id}
            className={`jrn-btn ${motif === option.id ? 'jrn-btn--primary' : 'jrn-btn--soft'}`}
            onClick={() => setMotif(option.id)}
            disabled={occupe}
          >
            {option.label}
          </button>
        ))}
      </div>

      {motif === 'rendez_vous' && (
        rendezVous.length === 0 ? (
          <p className="jrn-block__intro">Aucun rendez-vous relu pour l’instant. Relis d’abord le compte rendu du rendez-vous.</p>
        ) : (
          <div className="suivi-preparer__rdv">
            {rendezVous.length > 1 && (
              <label className="jrn-label">
                Rendez-vous
                <select value={rdvOrdinal ?? ''} onChange={(event) => setRdvOrdinal(Number(event.target.value))} disabled={occupe}>
                  {rendezVous.map((rdv) => (
                    <option key={rdv.ordinal} value={rdv.ordinal}>
                      {rdv.titre}{rdv.date ? ` · ${formaterDate(rdv.date)}` : ''}
                    </option>
                  ))}
                </select>
              </label>
            )}
            {rdvChoisi && (
              <div className="jrn-surface suivi-preparer__notes">
                <p className="jrn-label">
                  Tes notes du rendez-vous{rdvChoisi.date ? ` du ${formaterDate(rdvChoisi.date)}` : ''}
                </p>
                {rdvChoisi.resume && <p>{rdvChoisi.resume}</p>}
                {rdvChoisi.note && <p style={{ whiteSpace: 'pre-wrap' }}>{rdvChoisi.note}</p>}
                {rdvChoisi.questions.length > 0 && (
                  <ul>
                    {rdvChoisi.questions.map((q, index) => (
                      <li key={index}>{q.texte}{q.reponse ? ` — ${q.reponse}` : ''}</li>
                    ))}
                  </ul>
                )}
                {!rdvChoisi.resume && !rdvChoisi.note && rdvChoisi.questions.length === 0 && (
                  <p>Pas de note pour ce rendez-vous.</p>
                )}
              </div>
            )}
          </div>
        )
      )}

      {motif === 'rendez_vous' && aRegarder}
      {motif === 'rendez_vous' && (
        <p className="jrn-block__intro">Une fois la version préparée, « Proposer des ajustements » donne des idées de menus tirées de ces faits.</p>
      )}

      {motif === 'demande' && (
        <label className="jrn-label suivi-preparer__demande">
          Ce qu’elle a demandé (facultatif)
          <textarea
            rows={3}
            value={demande}
            onChange={(event) => setDemande(event.target.value)}
            disabled={occupe}
          />
        </label>
      )}

      {motif === 'changement_phase' && (
        phase ? (
          <p className="jrn-block__intro">
            Passage de « {phase.depuis} » à « {phase.nom} ». La nouvelle phase commence quand tu publies la version.
          </p>
        ) : (
          <p className="jrn-block__intro">Il n’y a pas de phase suivante à activer.</p>
        )
      )}

      {erreur && <p className="jrn-error" role="alert">{erreur}</p>}

      <div className="jrn-actions">
        <button
          type="button"
          className="jrn-btn jrn-btn--primary"
          disabled={!possible}
          onClick={() => onPreparer?.({
            motif,
            rdvOrdinal: motif === 'rendez_vous' ? rdvOrdinal : null,
            demande: motif === 'demande' ? demande.trim() : '',
          })}
        >
          {occupe ? 'Préparation…' : `Préparer la ${prochaineVersion}`}
        </button>
      </div>
    </section>
  );
}
